/**
 * Shared <SkillEditor scope> — Skills authoring surface.
 *
 * PR-D: skill-editor shared-component refactor (2026-05-13).
 *
 * Mounted by:
 *   - routes/studio/skills.tsx               (scope="tenant")
 *   - routes/sysadmin/studio/skills.tsx      (scope="sysadmin")
 *
 * Both surfaces are skill authoring editors with:
 *   - a search-filter row
 *   - a flat table list (Name / Display name / … / Actions)
 *   - a Sheet with the create/edit form (slug, displayName, description,
 *     instructions, toolDependencies, variables, category, isActive)
 *   - a confirm-delete AlertDialog
 *
 * The shared body renders both. Scope-specific affordances are gated
 * behind adapter flags (Bucket B). Save payload shape (Bucket C) is
 * built by the adapter's `serialize{Create,Update}Payload`.
 *
 * ─── SCOPE-DEVIATION TABLE ────────────────────────────────────────────────
 *
 *   Bucket A — Hook swaps (clean, parallel signatures)
 *   ──────────────────────────────────────────────────────────────────────
 *     tenant   : useStudioSkills(scope:'all')      → GET    /studio-skills
 *                useCreateStudioSkill              → POST   /studio-skills
 *                useUpdateStudioSkill              → PATCH  /studio-skills/:id
 *                useDeleteStudioSkill              → DELETE /studio-skills/:id
 *                useCloneStudioSkillFromSystem     → POST   /studio-skills/clone-from-system/:id
 *                  (returns StudioSkill; flat array under data.items)
 *     sysadmin : useSysAdminSystemSkills           → GET    /admin/platform/system-catalog/skills
 *                useCreateSystemSkill              → POST   /admin/platform/system-catalog/skills
 *                useUpdateSystemSkill(id)          → PATCH  /admin/platform/system-catalog/skills/:id
 *                  (wrapped to take id-in-payload at save time for
 *                   scope-agnostic save() contract — see adapters)
 *                useDeleteSystemSkill              → DELETE /admin/platform/system-catalog/skills/:id
 *                  (returns SystemSkill; flat array under data.items)
 *
 *   Bucket B — Affordances rendered only on one side
 *   ─────────────────────────────────────────────────
 *     Tenant only   : Scope filter chips (all / tenant / system / cloned)
 *                     Category dropdown (auto-built from list)
 *                     "{n} skills" row count chip on the right
 *                     System/Cloned source badges in the Display-name cell
 *                     Eye + Clone actions for read-only "System" rows
 *                     Skill Helper inline (Collapsible HelperPanel
 *                       kind="skill_helper") with set_metadata /
 *                       set_instructions / add_tool_dep / remove_tool_dep /
 *                       set_variables mutations
 *                     Provenance banner when editing a cloned-from-
 *                       system row (Round 4 fix #146 partial)
 *                     "variablesHaveBlankNames" save-gate + tooltip
 *                       (Round 6 fix STUDIO-R6-002)
 *                     Typed VariableEditor (flavor='skill') in the sheet
 *                     Real multi-select tool-dependency picker against
 *                       workspace_tools (Phase 5B #150) with
 *                       "missing-from-catalog" warning chips
 *                     "Updated" timestamp column + "Variables" count column
 *                     "Active" Switch in its own bordered row
 *                     View-system-skill read-only mode in the same Sheet
 *                       (viewingSystemId state) — disables every input
 *                       and shows a "Clone & customize" CTA
 *     Sysadmin only : "Tools" mini-badge column showing the first 3
 *                       toolDependencies + a "+N" overflow chip
 *                     "v{n}" version column
 *                     Clickable table rows (whole row enters edit mode)
 *                     JSON-text inputs for toolDependencies / variables /
 *                       examples (Textarea with font-mono)
 *                     "Active" Switch inline with Category (2-col grid)
 *                     Pre-save slug regex guard (/^[a-z0-9_.-]+$/)
 *                     "Deactivate" wording on the confirm dialog (soft-
 *                       delete vs tenant's hard-delete)
 *
 *   Bucket C — Save payload shape
 *   ──────────────────────────────
 *     Both bodies have: { name|slug, displayName, description, instructions,
 *                         toolDependencies, variables, category, isActive }
 *     Tenant uses field `name`; sysadmin uses field `slug` (same wire,
 *     different key — both servers accept theirs only).
 *     Sysadmin adds:    examples: unknown[]
 *     Sysadmin parses:  toolDependencies, variables, examples from JSON
 *                       text; tenant carries them as typed arrays already.
 *     The adapter's serialize{Create,Update}Payload returns a
 *     SerializeResult tagged union so JSON parse errors flow back as
 *     toast errors (sysadmin-only path).
 *
 *   Bucket D — Logic structure
 *   ──────────────────────────
 *     Save / delete handlers are structurally identical (validate +
 *     mutate + toast). They differ only in which adapter mutation
 *     they call. Wrapped behind `adapter.useSave()` + `adapter.useDelete()`.
 */
import { useState, useMemo, type ReactNode } from 'react';
import {
  Search,
  AlertTriangle,
  Plus,
  Pencil,
  Trash2,
  X,
  Eye,
  Copy,
  ExternalLink,
  Sparkles as SparklesIcon,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  VariableEditor,
  variablesHaveBlankNames,
  previewSkillTemplate,
} from '@/components/studio';
import { HelperPanel } from '@/components/studio/helper-panel';
import { useWorkspaceTools } from '@/lib/use-workspace-tools';
import { useHasPermission } from '@/lib/use-has-permission';
import type { PendingMutation } from '@/lib/use-studio-helper';
import { getAdapter } from './SkillEditor.adapters';
import {
  applySkillHelperMutation,
  rowScope,
} from './SkillEditor.helpers';
import type {
  NormalizedSkill,
  SkillDraft,
  SkillEditorScope,
} from './SkillEditor.types';

interface SkillEditorProps {
  scope: SkillEditorScope;
}

export function SkillEditor({ scope }: SkillEditorProps) {
  const adapter = getAdapter(scope);
  const { skills, loading, error } = adapter.useList();
  const { saveInFlight, save } = adapter.useSave();
  const del = adapter.useDelete();
  const clone = adapter.useCloneFromSystem();
  const canWriteSkills = useHasPermission('write', 'skills');
  const canDeleteSkills = useHasPermission('delete', 'skills');
  const canCreateOrEdit = scope === 'sysadmin' || canWriteSkills;
  const canDelete = scope === 'sysadmin' || canDeleteSkills;

  // Phase 5B (#150) — fetch workspace_tools for the real multi-select
  // tool-dependency input. Tenant-only; sysadmin uses JSON-text input.
  // Always called (hook ordering rule) — sysadmin just ignores the data.
  const { data: toolsData } = useWorkspaceTools({ scope: 'all' });
  const allToolNames = useMemo(
    () => (toolsData?.data ?? []).map((t) => t.name),
    [toolsData],
  );

  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<SkillDraft | null>(null);
  /** Tenant-only: when set, the sheet is viewing a system skill read-only. */
  const [viewingSystemId, setViewingSystemId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] =
    useState<NormalizedSkill | null>(null);
  const [scopeFilter, setScopeFilter] = useState<
    'all' | 'tenant' | 'system' | 'cloned'
  >('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const categories = useMemo(() => {
    if (!adapter.showCategoryDropdown) return [];
    return Array.from(new Set(skills.map((s) => s.category))).sort();
  }, [skills, adapter.showCategoryDropdown]);

  const filtered = useMemo(() => {
    let rows = skills;
    if (adapter.showScopeChips && scopeFilter !== 'all') {
      rows = rows.filter((s) => rowScope(s) === scopeFilter);
    }
    if (adapter.showCategoryDropdown && categoryFilter !== 'all') {
      rows = rows.filter((s) => s.category === categoryFilter);
    }
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (s) =>
        s.slug.toLowerCase().includes(q) ||
        s.displayName.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q),
    );
  }, [
    skills,
    search,
    scopeFilter,
    categoryFilter,
    adapter.showScopeChips,
    adapter.showCategoryDropdown,
  ]);

  const isEdit = draft?.id !== undefined && viewingSystemId === null;

  const handleSave = () => {
    if (!draft) return;
    if (!canCreateOrEdit) {
      toast.error('Permission required', {
        description: 'You can view Studio skills, but cannot create or edit them.',
      });
      return;
    }
    const result = isEdit
      ? adapter.serializeUpdatePayload(draft)
      : adapter.serializeCreatePayload(draft);
    if (!result.ok) {
      toast.error(result.message);
      return;
    }
    save({
      body: result.body,
      isEdit,
      id: draft.id,
      onSuccess: () => {
        const label = draft.displayName || draft.slug;
        toast.success(isEdit ? `Updated "${label}"` : `Created "${label}"`);
        setDraft(null);
        setViewingSystemId(null);
      },
      onError: (err) =>
        toast.error(isEdit ? 'Update failed' : 'Create failed', {
          description: err.message,
        }),
    });
  };

  const handleDelete = (s: NormalizedSkill) => {
    if (!canDelete) {
      toast.error('Permission required', {
        description: 'You can view Studio skills, but cannot delete them.',
      });
      setConfirmDelete(null);
      return;
    }
    del.run({
      id: s.id,
      onSuccess: () => {
        const msg = adapter.deleteSuccessTemplate
          .replace('${displayName}', s.displayName)
          .replace('${slug}', s.slug);
        toast.success(msg);
      },
      onError: (err) =>
        toast.error('Delete failed', { description: err.message }),
    });
    setConfirmDelete(null);
  };

  const handleClone = (s: NormalizedSkill) => {
    if (!clone) return;
    if (!canCreateOrEdit) {
      toast.error('Permission required', {
        description: 'You can view system skills, but cannot clone them.',
      });
      return;
    }
    clone.run({
      id: s.id,
      onSuccess: (resp) => {
        const newName =
          (resp as { data?: { name?: string } } | undefined)?.data?.name ??
          s.slug;
        toast.success(`Cloned to "${newName}"`, {
          description: 'Editable copy added to your skills.',
        });
        // Close the read-only view sheet if it was open via Clone-from-sheet.
        setDraft(null);
        setViewingSystemId(null);
      },
      onError: (err) =>
        toast.error('Clone failed', { description: err.message }),
    });
  };

  // ─── Render helpers ─────────────────────────────────────────────────

  const renderSourceBadges = (s: NormalizedSkill): ReactNode => {
    return (
      <>
        {s.scope === 'system' && (
          <Badge
            variant="outline"
            className="text-[9px] bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30"
          >
            System
          </Badge>
        )}
        {s.clonedFromSystemId && (
          <Badge
            variant="outline"
            className="text-[9px] bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30"
          >
            Cloned
          </Badge>
        )}
      </>
    );
  };

  const HeadingIcon = adapter.HeadingIcon;
  const isViewingSystem = viewingSystemId !== null;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <HeadingIcon className="h-4 w-4 text-muted-foreground" />
            {adapter.pageTitle}
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            {adapter.pageSubtitle}
          </p>
        </div>
        {canCreateOrEdit && (
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => setDraft(adapter.emptyDraft())}
          >
            <Plus className="h-3.5 w-3.5" /> {adapter.newButtonLabel}
          </Button>
        )}
      </div>

      {/* Search + scope/category filters row — tenant-only chips. The
          sysadmin pre-refactor surface had no search filter row above the
          table (only the page header + table), so we render the search
          input on both scopes (the input is universally useful) but hide
          the tenant-specific scope chips / category dropdown / row-count
          chip behind their adapter flags. */}
      {(adapter.showScopeChips ||
        adapter.showCategoryDropdown ||
        adapter.showRowCount ||
        scope === 'tenant') && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search skills…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>

          {/* Tenant: scope chips. */}
          {adapter.showScopeChips && (
            <div className="flex items-center gap-1.5">
              {(['all', 'tenant', 'system', 'cloned'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScopeFilter(s)}
                  className={`px-2 py-1 text-[11px] rounded border transition-colors capitalize ${
                    scopeFilter === s
                      ? s === 'system'
                        ? 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30'
                        : s === 'cloned'
                          ? 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30'
                          : s === 'tenant'
                            ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
                            : 'bg-foreground text-background border-foreground'
                      : 'bg-transparent text-muted-foreground border-border hover:text-foreground'
                  }`}
                >
                  {s === 'all' ? 'All scopes' : s}
                </button>
              ))}
            </div>
          )}

          {/* Tenant: category dropdown when >1 category. */}
          {adapter.showCategoryDropdown && categories.length > 1 && (
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="h-7 text-[11px] rounded border bg-background px-2 text-foreground"
              aria-label="Filter skills by category"
            >
              <option value="all">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          )}

          {/* Tenant: row count at the right. */}
          {adapter.showRowCount && skills.length > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums ml-auto">
              {filtered.length}{' '}
              {filtered.length === 1 ? 'skill' : 'skills'}
            </span>
          )}
        </div>
      )}

      {/* List body: loading / error / empty / table */}
      {loading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
            {adapter.loadingMessage}
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              {scope === 'sysadmin' ? 'Failed to load:' : 'Failed to load skills:'}{' '}
              {error.message}
            </div>
          </CardContent>
        </Card>
      ) : skills.length === 0 ? (
        // fix-wave-4 #1035 (STUDIO-SKILLS-002) — Pre-fix the empty state
        // rendered a Card with a `No skills` message and NO table, so
        // accessibility queries for column headers (`getByText('Name')`,
        // role=columnheader) found 0 nodes. We now render the canonical
        // table header row alongside the empty-state message so screen
        // readers + axe rules + matrix probes still see the column
        // contract even when the list is empty.
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={scope === 'tenant' ? 'w-44' : undefined}>
                    {scope === 'sysadmin' ? 'Slug' : 'Name'}
                  </TableHead>
                  <TableHead>Display name</TableHead>
                  {adapter.showToolsColumn && <TableHead>Tools</TableHead>}
                  <TableHead className={scope === 'tenant' ? 'w-28' : 'w-24'}>
                    Category
                  </TableHead>
                  <TableHead className="w-20">Active</TableHead>
                  {adapter.showVariablesColumn && (
                    <TableHead className="w-24">Variables</TableHead>
                  )}
                  {adapter.showVersionColumn && (
                    <TableHead className="w-16">Ver</TableHead>
                  )}
                  {adapter.showUpdatedColumn && (
                    <TableHead className="w-44">Updated</TableHead>
                  )}
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    {adapter.emptyStateMessage}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={scope === 'tenant' ? 'w-44' : undefined}>
                    {scope === 'sysadmin' ? 'Slug' : 'Name'}
                  </TableHead>
                  <TableHead>Display name</TableHead>
                  {adapter.showToolsColumn && <TableHead>Tools</TableHead>}
                  <TableHead className={scope === 'tenant' ? 'w-28' : 'w-24'}>
                    Category
                  </TableHead>
                  <TableHead className="w-20">Active</TableHead>
                  {adapter.showVariablesColumn && (
                    <TableHead className="w-24">Variables</TableHead>
                  )}
                  {adapter.showVersionColumn && (
                    <TableHead className="w-16">Ver</TableHead>
                  )}
                  {adapter.showUpdatedColumn && (
                    <TableHead className="w-44">Updated</TableHead>
                  )}
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow
                    key={s.id}
                    className={
                      adapter.clickableTableRows
                        ? 'cursor-pointer hover:bg-accent/30'
                        : undefined
                    }
                    onClick={
                      adapter.clickableTableRows
                        ? () => setDraft(adapter.draftFromRow(s))
                        : undefined
                    }
                  >
                    <TableCell className="font-mono text-xs">{s.slug}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium flex items-center gap-1.5">
                        {s.displayName}
                        {adapter.showSourceBadges && renderSourceBadges(s)}
                      </div>
                      {scope === 'tenant' && s.description && (
                        <div className="text-[11px] text-muted-foreground line-clamp-1 max-w-md">
                          {s.description}
                        </div>
                      )}
                    </TableCell>

                    {adapter.showToolsColumn && (
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {s.toolDependencies.slice(0, 3).map((t) => (
                            <Badge
                              key={t}
                              variant="secondary"
                              className="text-[9px] font-mono"
                            >
                              {t}
                            </Badge>
                          ))}
                          {s.toolDependencies.length > 3 && (
                            <Badge variant="outline" className="text-[9px]">
                              +{s.toolDependencies.length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    )}

                    <TableCell>
                      {scope === 'sysadmin' ? (
                        <span className="text-xs">{s.category}</span>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">
                          {s.category}
                        </Badge>
                      )}
                    </TableCell>

                    <TableCell>
                      {scope === 'sysadmin' ? (
                        <Badge
                          variant={s.isActive ? 'secondary' : 'outline'}
                          className="text-[10px]"
                        >
                          {s.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      ) : s.isActive ? (
                        <Badge variant="default" className="text-[10px]">
                          on
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-[10px]">
                          off
                        </Badge>
                      )}
                    </TableCell>

                    {adapter.showVariablesColumn && (
                      <TableCell className="text-xs text-muted-foreground tabular-nums">
                        {s.variables.length}
                      </TableCell>
                    )}

                    {adapter.showVersionColumn && (
                      <TableCell className="text-xs tabular-nums">
                        v{s.version ?? 1}
                      </TableCell>
                    )}

                    {adapter.showUpdatedColumn && (
                      <TableCell className="text-xs text-muted-foreground tabular-nums">
                        {new Date(s.updatedAt).toLocaleString()}
                      </TableCell>
                    )}

                    <TableCell
                      className="text-right"
                      onClick={
                        adapter.clickableTableRows
                          ? (e) => e.stopPropagation()
                          : undefined
                      }
                    >
                      {adapter.showSystemRowActions && s.scope === 'system' ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            title={`View ${s.displayName}`}
                            aria-label={`View ${s.displayName}`}
                            className="h-7 w-7 p-0"
                            onClick={() => {
                              setViewingSystemId(s.id);
                              setDraft({
                                id: s.id,
                                slug: s.slug,
                                displayName: s.displayName,
                                description: s.description,
                                instructions: s.instructions,
                                toolDependencies: s.toolDependencies,
                                variables: s.variables,
                                category: s.category,
                                isActive: s.isActive,
                              });
                            }}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          {canCreateOrEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title={`Clone & customize ${s.displayName}`}
                              aria-label={`Clone & customize ${s.displayName}`}
                              className="h-7 w-7 p-0 text-violet-600 dark:text-violet-300 hover:bg-violet-500/10"
                              onClick={() => handleClone(s)}
                              disabled={clone?.inFlight ?? false}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Pencil edit button — hidden on sysadmin
                              because the whole row is clickable, so the
                              extra button would duplicate the affordance.
                              Tenant: always shown. */}
                          {!adapter.clickableTableRows && canCreateOrEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title={`Quick edit ${s.displayName}`}
                              aria-label={`Quick edit ${s.displayName}`}
                              className="h-7 w-7 p-0"
                              onClick={() => setDraft(adapter.draftFromRow(s))}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {/* #153 — Open full detail page (tenant only).
                              The Sheet is for quick edits; the detail page
                              at /studio/skills/:id is the canonical surface
                              for variable + tool-dependency authoring,
                              versions, and "Used by Agents" lookup. */}
                          {scope === 'tenant' && (
                            <Link
                              to="/studio/skills/$skillId"
                              params={{ skillId: s.id }}
                              title={`Open ${s.displayName} in full editor`}
                              aria-label={`Open ${s.displayName} in full editor`}
                              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Link>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title={
                                scope === 'sysadmin'
                                  ? 'Deactivate'
                                  : `Delete ${s.displayName}`
                              }
                              aria-label={
                                scope === 'sysadmin'
                                  ? 'Deactivate'
                                  : `Delete ${s.displayName}`
                              }
                              className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                              onClick={() => setConfirmDelete(s)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Edit / Create / View-system sheet */}
      <Sheet
        open={draft !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDraft(null);
            setViewingSystemId(null);
          }
        }}
      >
        <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {isViewingSystem
                ? 'View system skill (read-only)'
                : isEdit
                  ? scope === 'sysadmin'
                    ? `Edit "${draft?.slug ?? ''}"`
                    : 'Edit skill'
                  : adapter.newSheetTitle}
            </SheetTitle>
            <SheetDescription>
              {isViewingSystem
                ? canCreateOrEdit
                  ? 'System skills are sysadmin-managed. Use Clone & customize to create an editable tenant copy.'
                  : 'System skills are sysadmin-managed. Your role can view them but cannot clone or edit them.'
                : isEdit
                  ? scope === 'sysadmin'
                    ? adapter.sheetDescription
                    : 'Update the skill instructions, dependencies, or metadata.'
                  : adapter.sheetDescription}
            </SheetDescription>
          </SheetHeader>
          {draft && (
            <div className="space-y-4 mt-4">
              {/* Provenance banner — tenant only, when editing a cloned row. */}
              {adapter.showProvenanceBanner &&
                draft.clonedFromSystemId &&
                !isViewingSystem && (
                  <div className="rounded border border-cyan-500/30 bg-cyan-500/5 px-3 py-2 text-xs text-cyan-700 dark:text-cyan-300 flex items-start gap-2">
                    <Copy className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <div>
                      <strong>Cloned from a system skill.</strong> You can
                      edit this copy freely; the original system skill is
                      unchanged. Provenance UUID:{' '}
                      <code className="font-mono">
                        {draft.clonedFromSystemId}
                      </code>
                    </div>
                  </div>
                )}

              {/* Slug + Category — tenant uses 2-col grid; sysadmin renders
                  slug full-width and Category appears in the Active row
                  below. */}
              {scope === 'tenant' ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="sk-name" className="text-xs">
                      Name (slug)
                    </Label>
                    <Input
                      id="sk-name"
                      value={draft.slug}
                      onChange={(e) =>
                        setDraft({ ...draft, slug: e.target.value })
                      }
                      placeholder={adapter.slugPlaceholder}
                      disabled={isEdit || !canCreateOrEdit}
                      className="font-mono text-xs h-8"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sk-category" className="text-xs">
                      Category
                    </Label>
                    <Input
                      id="sk-category"
                      value={draft.category}
                      onChange={(e) =>
                        setDraft({ ...draft, category: e.target.value })
                      }
                      placeholder={adapter.categoryPlaceholder}
                      disabled={isViewingSystem || !canCreateOrEdit}
                      className="text-xs h-8"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="sk-name" className="text-xs">
                    Slug
                  </Label>
                  <Input
                    id="sk-name"
                    value={draft.slug}
                    onChange={(e) =>
                      setDraft({ ...draft, slug: e.target.value })
                    }
                    disabled={isEdit || !canCreateOrEdit}
                    placeholder={adapter.slugPlaceholder}
                    className="font-mono text-xs h-8"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="sk-display" className="text-xs">
                  Display name
                </Label>
                <Input
                  id="sk-display"
                  value={draft.displayName}
                  onChange={(e) =>
                    setDraft({ ...draft, displayName: e.target.value })
                  }
                  placeholder={adapter.displayNamePlaceholder}
                  disabled={isViewingSystem || !canCreateOrEdit}
                  className="text-sm h-8"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="sk-description" className="text-xs">
                  Description
                </Label>
                <Textarea
                  id="sk-description"
                  value={draft.description}
                  onChange={(e) =>
                    setDraft({ ...draft, description: e.target.value })
                  }
                  placeholder={adapter.descriptionPlaceholder}
                  rows={2}
                  disabled={isViewingSystem || !canCreateOrEdit}
                  className="text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="sk-instructions" className="text-xs">
                  Instructions {scope === 'tenant' ? '(Markdown)' : '(markdown)'}
                </Label>
                <Textarea
                  id="sk-instructions"
                  value={draft.instructions}
                  onChange={(e) =>
                    setDraft({ ...draft, instructions: e.target.value })
                  }
                  placeholder={adapter.instructionsPlaceholder}
                  rows={10}
                  disabled={isViewingSystem || !canCreateOrEdit}
                  className={
                    scope === 'sysadmin'
                      ? 'text-xs font-mono'
                      : 'text-sm font-mono'
                  }
                  spellCheck={scope === 'sysadmin' ? false : undefined}
                />
              </div>

              {/* Tool dependencies — tenant: real multi-select with
                  catalog validation; sysadmin: JSON-text mirror. */}
              {adapter.showToolDependenciesPicker && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Tool dependencies</Label>
                  <div className="border rounded p-2 max-h-48 overflow-y-auto space-y-1">
                    {allToolNames.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground italic">
                        No tools in catalog yet.
                      </p>
                    ) : (
                      allToolNames.map((name) => {
                        const checked =
                          draft.toolDependencies.includes(name);
                        return (
                          <label
                            key={name}
                            className="flex items-center gap-2 text-xs hover:bg-accent/30 px-1 py-0.5 cursor-pointer rounded"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                setDraft({
                                  ...draft,
                                  toolDependencies: checked
                                    ? draft.toolDependencies.filter(
                                        (t) => t !== name,
                                      )
                                    : [...draft.toolDependencies, name],
                                })
                              }
                              disabled={isViewingSystem || !canCreateOrEdit}
                              className="h-3 w-3"
                            />
                            <span className="font-mono">{name}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  {/* Missing-from-catalog warning (legacy free-text skills
                      or tools removed since the skill was authored). */}
                  {draft.toolDependencies.some(
                    (t) => !allToolNames.includes(t),
                  ) && (
                    <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                      Missing from catalog:{' '}
                      {draft.toolDependencies
                        .filter((t) => !allToolNames.includes(t))
                        .map((t) => (
                          <Badge
                            key={t}
                            variant="outline"
                            className="ml-1 text-[9px] bg-amber-500/10 text-amber-700 border-amber-500/30"
                          >
                            {t}
                          </Badge>
                        ))}
                    </div>
                  )}
                  <div className="text-[11px] text-muted-foreground">
                    The agent runtime warns if attached skills reference
                    tools the agent doesn't have granted.
                  </div>
                </div>
              )}

              {adapter.showToolDependenciesText && (
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Tool dependencies (JSON string array)
                  </Label>
                  <Input
                    value={draft.toolDependenciesText ?? '[]'}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        toolDependenciesText: e.target.value,
                      })
                    }
                    className="text-xs font-mono h-8"
                  />
                </div>
              )}

              {/* Typed VariableEditor — tenant only. */}
              {adapter.showVariableEditor && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Variables</Label>
                  <VariableEditor
                    flavor="skill"
                    value={draft.variables}
                    onChange={(next) =>
                      setDraft({ ...draft, variables: next })
                    }
                    disabled={isViewingSystem || !canCreateOrEdit}
                  />
                </div>
              )}

              {/* Live preview — #150 phase 5B follow-up. Shows how the
                  `{{var}}` placeholders in the instructions body will
                  substitute at run-time and surfaces two kinds of
                  template hygiene issues:
                    - declared-but-unused  variables (`missing`)
                    - placeholder-without-declaration (`undeclared`)
                  Tenant-only because the helper-driven live preview
                  pairs with the typed VariableEditor; sysadmin authors
                  use the JSON-text mirror and don't need the preview. */}
              {adapter.showVariableEditor && draft.variables.length > 0 && (
                <SkillInstructionsPreview
                  instructions={draft.instructions}
                  variables={draft.variables}
                />
              )}

              {/* JSON-text Variables — sysadmin only. */}
              {adapter.showVariablesText && (
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Variables (JSON array of{' '}
                    {'{name,type,description,required?}'})
                  </Label>
                  <Textarea
                    value={draft.variablesText ?? '[]'}
                    onChange={(e) =>
                      setDraft({ ...draft, variablesText: e.target.value })
                    }
                    rows={4}
                    className="text-xs font-mono"
                    spellCheck={false}
                  />
                </div>
              )}

              {/* JSON-text Examples — sysadmin only. */}
              {adapter.showExamplesText && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Examples (JSON array)</Label>
                  <Textarea
                    value={draft.examplesText ?? '[]'}
                    onChange={(e) =>
                      setDraft({ ...draft, examplesText: e.target.value })
                    }
                    rows={3}
                    className="text-xs font-mono"
                    spellCheck={false}
                  />
                </div>
              )}

              {/* Active switch — placement varies by scope. */}
              {adapter.activeSwitchPlacement === 'bordered-row' ? (
                <div className="flex items-center justify-between rounded border p-2">
                  <div className="text-sm">Active</div>
                  <Switch
                    checked={draft.isActive}
                    onCheckedChange={(v) => setDraft({ ...draft, isActive: v })}
                    disabled={isViewingSystem}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Category</Label>
                    <Input
                      value={draft.category}
                      onChange={(e) =>
                        setDraft({ ...draft, category: e.target.value })
                      }
                      className="text-sm h-8"
                    />
                  </div>
                  <div className="flex items-center gap-2 h-8">
                    <Switch
                      checked={draft.isActive}
                      onCheckedChange={(v) =>
                        setDraft({ ...draft, isActive: v })
                      }
                    />
                    <span className="text-xs">Active</span>
                  </div>
                </div>
              )}

              {/* Skill Helper inline — tenant only, hidden in read-only view. */}
              {adapter.showHelperPanel && !isViewingSystem && (
                <Collapsible className="rounded border bg-violet-500/5">
                  <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-violet-700 dark:text-violet-300 hover:bg-violet-500/10 rounded">
                    <SparklesIcon className="h-3.5 w-3.5" />
                    Draft with Skill Helper (AI)
                  </CollapsibleTrigger>
                  <CollapsibleContent className="border-t">
                    <HelperPanel
                      kind="skill_helper"
                      intro="Tell me what skill you need. I'll ask a few clarifying questions, then draft the instructions, dependencies, and variables — you Accept or Reject each suggestion."
                      className="border-0 rounded-none min-h-[320px]"
                      canvasState={
                        {
                          name: draft.slug,
                          displayName: draft.displayName,
                          description: draft.description,
                          instructions: draft.instructions,
                          toolDependencies: draft.toolDependencies,
                          variables: draft.variables,
                        } as Record<string, unknown>
                      }
                      onApplyMutation={(m: PendingMutation) => {
                        // #1095 — Pre-fix this handler called multiple
                        //   setDraft({ ...draft, foo: x });
                        //   setDraft({ ...draft, bar: y });
                        // statements in sequence inside one event tick.
                        // Each setDraft captured the SAME stale `draft`
                        // from the React closure, so only the last call's
                        // field landed and every other field was silently
                        // overwritten back to its pre-Accept value. With
                        // displayName left blank the "Create skill"
                        // button stayed disabled, so the helper appeared
                        // to do nothing.
                        //
                        // Fix:
                        //   1. Coalesce ALL field updates into ONE patch
                        //      and apply via functional setState — never
                        //      reads stale closure.
                        //   2. set_metadata({ name, description }) now
                        //      also populates `displayName` (the helper's
                        //      `name` semantically maps to the human-
                        //      readable name, not the slug). Without this
                        //      a single set_metadata could not enable the
                        //      Create button.
                        //
                        // `add_tool_dep` / `remove_tool_dep` need the
                        // current draft's `toolDependencies` to compute
                        // the new array, so they're inlined here with
                        // their own functional setStates (the patch
                        // helper doesn't compute against existing state).
                        const a = m.args as Record<string, unknown>;
                        if (
                          m.toolName === 'add_tool_dep' &&
                          a.slug != null
                        ) {
                          const slug = String(a.slug);
                          setDraft((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  toolDependencies: Array.from(
                                    new Set([...prev.toolDependencies, slug]),
                                  ),
                                }
                              : prev,
                          );
                          return;
                        }
                        if (
                          m.toolName === 'remove_tool_dep' &&
                          a.slug != null
                        ) {
                          const slug = String(a.slug);
                          setDraft((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  toolDependencies: prev.toolDependencies.filter(
                                    (s) => s !== slug,
                                  ),
                                }
                              : prev,
                          );
                          return;
                        }
                        // set_metadata / set_instructions / set_variables /
                        // set_guardrails — derive a single patch and apply
                        // atomically. Compute the patch from `prev` (not the
                        // closure draft) so set_guardrails, which APPENDS to
                        // instructions, always folds into the current value
                        // instead of a stale one.
                        setDraft((prev) => {
                          if (!prev) return prev;
                          const patch = applySkillHelperMutation(
                            m.toolName,
                            a,
                            prev,
                          );
                          if (Object.keys(patch).length === 0) return prev;
                          return { ...prev, ...patch };
                        });
                      }}
                    />
                  </CollapsibleContent>
                </Collapsible>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDraft(null);
                    setViewingSystemId(null);
                  }}
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  {isViewingSystem ? 'Close' : 'Cancel'}
                </Button>
                {isViewingSystem && canCreateOrEdit ? (
                  <Button
                    size="sm"
                    className="gap-1.5"
                    disabled={(clone?.inFlight ?? false) || !canCreateOrEdit}
                    onClick={() => {
                      if (!viewingSystemId || !clone) return;
                      if (!canCreateOrEdit) {
                        toast.error('Permission required', {
                          description:
                            'You can view system skills, but cannot clone them.',
                        });
                        return;
                      }
                      clone.run({
                        id: viewingSystemId,
                        onSuccess: (resp) => {
                          const newName =
                            (
                              resp as
                                | { data?: { name?: string } }
                                | undefined
                            )?.data?.name ?? draft.slug;
                          toast.success(`Cloned to "${newName}"`, {
                            description:
                              'Editable copy added to your skills.',
                          });
                          setDraft(null);
                          setViewingSystemId(null);
                        },
                        onError: (err) =>
                          toast.error('Clone failed', {
                            description: err.message,
                          }),
                      });
                    }}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {clone?.inFlight ? 'Cloning…' : 'Clone & customize'}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={
                      !draft.slug ||
                      !draft.displayName ||
                      !draft.description ||
                      !draft.instructions ||
                      (scope === 'tenant' &&
                        variablesHaveBlankNames(draft.variables)) ||
                      saveInFlight
                      || !canCreateOrEdit
                    }
                    title={
                      scope === 'tenant' &&
                      variablesHaveBlankNames(draft.variables)
                        ? 'One or more variables are missing a name.'
                        : undefined
                    }
                  >
                    {saveInFlight
                      ? scope === 'sysadmin' && !isEdit
                        ? 'Creating…'
                        : 'Saving…'
                      : isEdit
                        ? 'Save changes'
                        : scope === 'sysadmin'
                          ? 'Create skill'
                          : 'Create skill'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete confirm */}
      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{adapter.deleteDialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {scope === 'sysadmin' ? (
                <>
                  Soft-deletes{' '}
                  <code className="bg-muted px-1 rounded">
                    {confirmDelete?.slug}
                  </code>
                  . {adapter.deleteConfirmMessage}
                </>
              ) : (
                <>
                  Permanently delete{' '}
                  <code className="bg-muted px-1 rounded">
                    {confirmDelete?.slug}
                  </code>
                  ? {adapter.deleteConfirmMessage}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
            >
              {adapter.deleteActionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── SkillInstructionsPreview — #150 phase 5B follow-up ─────────────────────
//
// Renders the instructions body with `{{var}}` placeholders substituted,
// plus warning chips for declared-but-unused variables and placeholders
// that are referenced in the body but never declared. Lives in this file
// (vs a separate component) because it is tightly coupled to the
// `SkillDraft.variables` shape and only mounted from this sheet.
function SkillInstructionsPreview({
  instructions,
  variables,
}: {
  instructions: string;
  variables: import('@/lib/use-studio-skills').StudioSkillVariable[];
}) {
  const { rendered, missing, undeclared } = useMemo(
    () => previewSkillTemplate(instructions, variables),
    [instructions, variables],
  );
  // Nothing useful to preview if the body is empty and no warnings fire.
  const hasContent =
    rendered.trim().length > 0 || missing.length > 0 || undeclared.length > 0;
  if (!hasContent) return null;
  return (
    <Collapsible className="rounded border bg-muted/30">
      <CollapsibleTrigger
        className="flex w-full items-center justify-between px-3 py-2 text-xs font-medium hover:bg-muted/50 rounded"
        aria-label="Toggle live preview"
      >
        <span className="flex items-center gap-2">
          <Eye className="h-3.5 w-3.5" />
          Live preview
        </span>
        {(missing.length > 0 || undeclared.length > 0) && (
          <span className="flex items-center gap-1">
            {undeclared.length > 0 && (
              <Badge
                variant="outline"
                className="text-[9px] bg-amber-500/10 text-amber-700 border-amber-500/30"
              >
                {undeclared.length} undeclared
              </Badge>
            )}
            {missing.length > 0 && (
              <Badge
                variant="outline"
                className="text-[9px] bg-muted text-muted-foreground"
              >
                {missing.length} unused
              </Badge>
            )}
          </span>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t px-3 py-2 space-y-2">
        <pre className="text-[11px] font-mono whitespace-pre-wrap break-words bg-background border rounded p-2 max-h-40 overflow-auto">
          {rendered || (
            <span className="text-muted-foreground italic">
              (instructions body is empty)
            </span>
          )}
        </pre>
        {undeclared.length > 0 && (
          <p className="text-[10px] text-amber-700 dark:text-amber-400">
            Found placeholders with no matching variable declaration:{' '}
            <code className="font-mono">{undeclared.join(', ')}</code>
          </p>
        )}
        {missing.length > 0 && (
          <p className="text-[10px] text-muted-foreground">
            Declared but never referenced in the body:{' '}
            <code className="font-mono">{missing.join(', ')}</code>
          </p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
